<script lang="ts" setup>
import SuccessIcon from '@/assets/icons/success_icon.png'
import WarningIcon from '@/assets/icons/warning_icon.png'
import ErrorIcon from '@/assets/icons/error_icon.png'
import NormalIcon from '@/assets/icons/normal_icon.png'
import MTransition from '@/components/common/m-transition.vue'
import HeaderBg from '@/assets/images/m_popup_header_bg.png'
import HeaderLightBg from '@/assets/images/m_popup_header_light_bg.png'
import { useTimeout } from '@/hooks/useTimer'

const props = withDefaults(
  defineProps<{
    theme?: 'light' | 'dark'
    icon?: 'success' | 'warning' | 'error' | 'normal'
    title?: string
    subTitle?: string
    content?: string
    confirmBtnText?: string
    cancelBtnText?: string
    skipBtnText?: string
    isRotate?: boolean
    isClose?: boolean
    equalBtn?: boolean
    hideCancelBtn?: boolean
    hideSkipBtn?: boolean
    hideConfirmBtn?: boolean
    countDownProps?: {
      time: number
    }
    onConfirm?: () => Promise<void>
    onCancel?: () => Promise<void>
  }>(),
  {
    theme: 'light',
  },
)

const countDownPropsRef = reactive(props.countDownProps || { time: undefined })
const { timer, clearTimer } = useTimeout()
const popupCls = computed(() => (props.isRotate ? 'm-rotate' : 'm-popup'))
const bgImage = computed(() => (props.theme === 'dark' ? HeaderBg : HeaderLightBg))

const state = reactive({
  isVisible: false,
  type: '',
})

const { isVisible, type } = toRefs(state)

/**
 * 改变组件显隐
 * @param value true(显示) / false(隐藏)
 */
const setVisible = (value: boolean) => {
  isVisible.value = value
}

/**
 * 确认
 */
const handleConfirm = async () => {
  if (props.onConfirm) {
    await props.onConfirm()
    isVisible.value = false
    return
  }
  isVisible.value = false
  type.value = 'confirm'
}

/**
 * 取消
 */
const handleCancel = async () => {
  if (props.onCancel) {
    await props.onCancel()
    isVisible.value = false
    return
  }
  isVisible.value = false
  type.value = 'cancel'
}

const handleSkip = () => {
  clearTimer()
  countDownPropsRef.time = undefined
  handleCancel()
}

const handleClose = () => {
  isVisible.value = false
}

const handleCountDown = () => {
  if (countDownPropsRef.time !== undefined) {
    timer.value = setInterval(() => {
      if (countDownPropsRef.time !== undefined) {
        if (countDownPropsRef.time <= 0) {
          clearTimer()
          handleSkip()
          return
        }
        countDownPropsRef.time--
      }
    }, 1000)
  }
}

onMounted(() => {
  if (countDownPropsRef.time) {
    handleCountDown()
  }
})

defineExpose({
  setVisible,
  state,
})
</script>

<template>
  <m-transition name="fade">
    <div
      v-show="isVisible"
      :class="[popupCls, `${popupCls}--${props.theme}`, equalBtn ? `${popupCls}--equal-btn` : '']"
      @click.self="isClose ? handleClose() : () => {}"
    >
      <div class="m-popup-box">
        <div
          class="header image-bg"
          :style="{ backgroundImage: `url(${bgImage})` }"
        >
          <slot name="icon">
            <img
              v-if="icon === 'success'"
              class="icon"
              :src="SuccessIcon"
            />
            <img
              v-else-if="icon === 'error'"
              class="icon"
              :src="ErrorIcon"
            />
            <img
              v-else-if="icon === 'warning'"
              class="icon"
              :src="WarningIcon"
            />
            <img
              v-else
              class="icon"
              :src="NormalIcon"
            />
          </slot>
          <h3
            v-if="title"
            class="title"
            v-html="title"
          ></h3>
        </div>
        <div class="body">
          <p
            v-if="subTitle"
            class="sub-title"
            v-html="subTitle"
          ></p>
          <p
            v-if="content"
            class="content-text"
            v-html="content"
          ></p>
          <div
            v-if="$slots.comContent"
            class="component-content"
          >
            <slot name="comContent"></slot>
          </div>
          <div class="button-group">
            <button
              v-if="cancelBtnText && !skipBtnText && !hideCancelBtn"
              class="btn cancel-btn"
              @click.stop="handleCancel"
              v-html="cancelBtnText"
            ></button>
            <button
              v-if="skipBtnText && !cancelBtnText && !hideSkipBtn"
              class="btn skip-btn"
              @click.stop="handleSkip"
              v-html="
                countDownPropsRef && countDownPropsRef.time !== undefined
                  ? `${countDownPropsRef.time}S${skipBtnText}`
                  : skipBtnText
              "
            ></button>
            <button
              v-if="!hideConfirmBtn"
              class="btn confirm-btn"
              @click.stop="handleConfirm"
              v-html="confirmBtnText || 'OK'"
            ></button>
          </div>
        </div>
      </div>
    </div>
  </m-transition>
</template>

<style scoped lang="scss">
/* Define CSS variables for themes */
.m-popup--light,
.m-rotate--light {
  --popup-title-color: #{$font-black-color};
  --popup-line-color: #64c7ff;
  --popup-body-bg: linear-gradient(180deg, #64c7ff 0%, #{$font-white-color} 100%);
  --popup-subtitle-color: #{$font-sub-color};
  --popup-content-color: #{$primary-blue-color};
}

.m-popup--dark,
.m-rotate--dark {
  --popup-title-color: #{$font-white-color};
  --popup-line-color: #{$primary-black-color};
  --popup-body-bg: #{$primary-black-color};
  --popup-subtitle-color: #{$font-sub-color};
  --popup-content-color: #{$primary-blue-color};
}

.m-popup {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 20000 !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  --width: 34.4rem;
}
.m-rotate {
  position: fixed;
  transform: rotate(90deg);
  top: calc(50% - 50dvw);
  left: calc(50% - 50dvh);
  z-index: 20000 !important;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  --width: 34.4rem;
  width: 100dvh;
  height: 100dvw;
}
.m-popup--equal-btn,
.m-rotate--equal-btn {
  .m-popup-box .button-group .btn {
    flex: 1;
    padding: 1.5rem 0 !important;
  }
}
.m-popup-box {
  width: var(--width);
  .header {
    position: relative;
    width: var(--width);
    height: 5.5rem;
    display: flex;
    flex-direction: column;
    padding: 2rem 13.2rem 1.1rem 2rem;
    box-sizing: border-box;
    background-size: 100%;
    .icon {
      position: absolute;
      right: 0;
      top: 0;
      width: 10rem;
      height: 10rem;
      transform: translateY(-50%);
    }
    .drone-icon {
      width: 8.8rem;
      height: 5.8rem;
      transform: translateY(-25%);
    }
    .title {
      font-size: 1.6rem;
      font-weight: 700;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      line-clamp: 1;
      -webkit-box-orient: vertical;
      color: var(--popup-title-color);
    }
  }
  .header::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -0.1rem;
    width: 99.6%;
    height: 0.2rem;
    background: var(--popup-line-color);
  }
  .body {
    width: var(--width);
    height: auto;
    border-radius: 0rem 0rem 1rem 1rem;
    display: flex;
    flex-direction: column;
    padding: 0.1rem 2rem 2rem;
    box-sizing: border-box;
    background: var(--popup-body-bg);

    .sub-title,
    .content-text {
      max-width: 30.4rem;
      white-space: pre-wrap; /* 允许换行 */
      word-wrap: break-word; /* 在单词边界换行 */
    }
    .sub-title {
      font-size: 1.4rem;
      font-weight: 400;
      line-height: 2.2rem;
      color: var(--popup-subtitle-color);
    }
    .content-text {
      font-size: 1.6rem;
      font-weight: 700;
      margin-top: 1rem;
      color: var(--popup-content-color);
    }
    .component-content {
      margin-top: 1.2rem;
    }
    .button-group {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-top: 2.2rem;
      .btn {
        display: flex;
        padding: 1.5rem 2.4rem;
        justify-content: center;
        align-items: center;
        border-radius: 1rem;
        background: $font-blue-color;
        color: $font-white-color;
        text-align: center;
        font-size: 1.6rem;
        font-weight: 700;
        line-height: 2rem;
        white-space: nowrap;
      }
      .confirm-btn {
        padding: 1.5rem 4rem;
      }
      .cancel-btn {
        background: linear-gradient(180deg, #f96 0%, #ff6629 100%);
      }
      .skip-btn {
        background: $sub-color;
      }
      .cancel-btn,
      .skip-btn {
        margin-right: 2rem;
      }
    }
  }
}
</style>
